import { CommunityHeader, HeroImage } from '@core/components';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import {
  ModerationTotalCasesPanel,
  ModerationCasesOpenPanel,
  ModerationActiveModeratorsPanel,
  ModerationCasesCompletedPanel,
} from '@moderation/components/ModerationStatsPanel';
import type { ModerationCommunity } from '@moderation/domain/ModerationCommunity';
import { Heading, Dropdown } from '@soteria/design-system-ve';
import { useState } from 'react';

import * as styles from './CommunityModerationHeaderSection.css';

export const TIME_RANGE_OPTIONS = [
  { id: '24h', label: 'Last 24 Hours' },
  { id: '48h', label: 'Last 48 Hours' },
  { id: '72h', label: 'Last 72 Hours' },
] as const;

export type TimeRange = (typeof TIME_RANGE_OPTIONS)[number]['id'];

export interface ModerationStats {
  totalCases: number;
  casesOpen: number;
  activeModerators: number;
  casesCompleted: number;
}

// Default stats constant to avoid recreating object on every render
// Using zeros to avoid misleading users with fake data when stats aren't available
export const DEFAULT_MODERATION_STATS: ModerationStats = {
  totalCases: 0,
  casesOpen: 0,
  activeModerators: 0,
  casesCompleted: 0,
};

interface CommunityModerationHeaderSectionProps {
  community: ModerationCommunity | null;
  stats?: ModerationStats;
  isLoading?: boolean;
}

/**
 * Header section component for moderation pages with gradient transition
 * Contains community name, image, and moderation stats panels
 * Background image is handled by the global useHeroBackground hook
 */
function CommunityModerationHeaderSectionComponent({ community, stats, isLoading = false }: CommunityModerationHeaderSectionProps) {
  const name = community?.name || '';
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>('24h');
  const isStatsCardsHidden = useIsFeatureEnabled(FEATURE_FLAGS.HIDE_MODERATION_STATS_CARDS);

  // Default stats if none provided
  const moderationStats = stats || DEFAULT_MODERATION_STATS;

  const handleTimeRangeChange = (value: string) => {
    const timeRangeOption = TIME_RANGE_OPTIONS.find(option => option.id === value);

    if (timeRangeOption) {
      setSelectedTimeRange(timeRangeOption.id);
    }
    // TODO: In the future, this would trigger a data refetch with the new time range
  };

  // V1: Original implementation with stats in header
  const v1Component = (
    <div className={styles.heroSection} data-testid="community-moderation-header-v1">
      {/* Back Button Placeholder - Invisible but takes up same space as detail pages for consistent positioning */}
      <div className={styles.backButtonPlaceholder} />

      {/* Gradient Overlay */}
      <div className={styles.heroOverlay} />

      <div className={styles.heroContent}>
        {/* Left Column - Image */}
        <HeroImage isLoading={isLoading} logoUrl={community?.logoUrl} name={name} />

        {/* Right Column - Content */}
        <div className={styles.heroContentRight}>
          {/* Community Title */}
          <Heading className={styles.communityTitle} level="h1">
            {name} Moderation
          </Heading>

          {!isStatsCardsHidden && (
            <>
              {/* Time Range Filter - On its own line below title */}
              <div className={styles.timeRangeContainer}>
                <label className={styles.timeRangeLabel} htmlFor="timeRangeDropdown">
                  Showing data for:
                </label>
                <Dropdown
                  className={styles.timeRangeDropdown}
                  id="timeRangeDropdown"
                  options={[...TIME_RANGE_OPTIONS]}
                  selectedItem={selectedTimeRange}
                  size="medium"
                  onSelectionChange={handleTimeRangeChange}
                />
              </div>

              {/* 4-Panel Stats Grid */}
              <div className={styles.fourPanelContainer}>
                <div className={styles.fourPanelGrid}>
                  {/* Total Cases Panel */}
                  <ModerationTotalCasesPanel isLoading={isLoading} totalCases={moderationStats.totalCases} />

                  {/* Cases Open Panel */}
                  <ModerationCasesOpenPanel casesOpen={moderationStats.casesOpen} isLoading={isLoading} />

                  {/* Active Moderators Panel */}
                  <ModerationActiveModeratorsPanel activeModerators={moderationStats.activeModerators} isLoading={isLoading} />

                  {/* Cases Completed Panel */}
                  <ModerationCasesCompletedPanel casesCompleted={moderationStats.casesCompleted} isLoading={isLoading} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // V2: Use new CommunityHeader design system component (stats moved to page body)
  // Uses title-only variant - no subtitle needed for moderation pages
  return (
    <CommunityHeader
      v1Component={v1Component}
      v2Props={{
        'data-testid': 'community-moderation-header-v2',
        title: `${name} Moderation`,
      }}
    />
  );
}

export const CommunityModerationHeaderSection = withAppInsightsTracking(CommunityModerationHeaderSectionComponent, 'CommunityModerationHeaderSection');
